import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { eventsApi, type ClassEventResponse } from '@/api/endpoints/events'
import { classesApi } from '@/api/endpoints/classes'
import { useUI } from '@/stores/ui'

interface Props {
  open: boolean
  onClose: () => void
  classId: string
  events: ClassEventResponse[]
  onUpdated: () => void
}

export default function BulkEditModal({ open, onClose, classId, events, onUpdated }: Props) {
  const addToast = useUI((state) => state.addToast)
  const [mode, setMode] = useState<'remove-notes' | 'bulk-edit'>('remove-notes')
  const [confirmDelete, setConfirmDelete] = useState('')
  // What "Remove All Notes" clears: the class's own notes, every event's notes, or both
  const [removeWhat, setRemoveWhat] = useState<'class' | 'events' | 'both'>('both')
  const [submitting, setSubmitting] = useState(false)
  
  // Bulk edit state
  const [editTarget, setEditTarget] = useState<'topics' | 'notes'>('topics')
  const [editMode, setEditMode] = useState<'prefix' | 'suffix'>('prefix')
  const [editText, setEditText] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  useEffect(() => {
    if (!open) {
      setMode('remove-notes')
      setConfirmDelete('')
      setRemoveWhat('both')
      setEditTarget('topics')
      setEditMode('prefix')
      setEditText('')
      setStartDate('')
      setEndDate('')
    }
  }, [open])

  const handleRemoveAllNotes = async () => {
    if (confirmDelete !== 'delete') {
      addToast({ message: 'Please type "delete" to confirm', type: 'error' })
      return
    }

    setSubmitting(true)
    try {
      const eventIds = events.filter(e => e.notes).map(e => e.id)
      if (removeWhat !== 'events') await classesApi.update(classId, { notes: null })
      if (removeWhat !== 'class' && eventIds.length) await eventsApi.bulkUpdate(eventIds, { notes: '' })
      addToast({
        message: removeWhat === 'class' ? 'Class notes removed'
          : removeWhat === 'events' ? `Notes removed from ${eventIds.length} event(s)`
          : `Class notes and notes on ${eventIds.length} event(s) removed`,
        type: 'success',
      })
      onUpdated()
      onClose()
    } catch (error: any) {
      console.error('Failed to remove notes:', error)
      addToast({
        message: error.response?.data?.detail || 'Failed to remove notes',
        type: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleBulkEdit = async () => {
    if (!startDate || !endDate) {
      addToast({ message: 'Please select both start and end dates', type: 'error' })
      return
    }

    if (!editText.trim()) {
      addToast({ message: 'Please enter text to add', type: 'error' })
      return
    }

    setSubmitting(true)
    try {
      const start = new Date(startDate)
      const end = new Date(endDate)
      
      // Filter events by date range
      const eventsInRange = events.filter(ev => {
        if (!ev.event_date) return false
        const evDate = new Date(ev.event_date)
        return evDate >= start && evDate <= end
      })

      if (eventsInRange.length === 0) {
        addToast({ message: 'No events found in the selected date range', type: 'warning' })
        setSubmitting(false)
        return
      }

      // Update each event
      const updatePromises = eventsInRange.map(async (ev) => {
        const currentValue = editTarget === 'topics' ? ev.topic : (ev.notes || '')
        const newValue = editMode === 'prefix' 
          ? `${editText}${currentValue}`  // keep spaces: "Confirmation Class - " must stay separated
          : `${currentValue}${editText}`
        
        if (editTarget === 'topics') {
          return eventsApi.update(ev.id, { topic: newValue })
        } else {
          return eventsApi.update(ev.id, { notes: newValue })
        }
      })

      await Promise.all(updatePromises)
      
      addToast({ 
        message: `Updated ${eventsInRange.length} event${eventsInRange.length !== 1 ? 's' : ''}`, 
        type: 'success' 
      })
      onUpdated()
      onClose()
    } catch (error: any) {
      console.error('Failed to bulk edit:', error)
      addToast({
        message: error.response?.data?.detail || 'Failed to bulk edit events',
        type: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          aria-modal="true"
          role="dialog"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose()
          }}
        >
          <motion.div
            className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 hover-raise mx-4"
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Bulk Edit Class Details</h2>
              <button className="btn-secondary text-sm focus-ring" onClick={onClose}>Close</button>
            </div>

            <div className="mb-4 flex gap-2 border-b border-gray-200">
              <button
                className={`px-4 py-2 text-sm font-medium transition-colors focus-ring ${
                  mode === 'remove-notes'
                    ? 'border-b-2 border-yellow-500 text-yellow-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setMode('remove-notes')}
              >
                Remove All Notes
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium transition-colors focus-ring ${
                  mode === 'bulk-edit'
                    ? 'border-b-2 border-yellow-500 text-yellow-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setMode('bulk-edit')}
              >
                Bulk Edit Events
              </button>
            </div>

            {mode === 'remove-notes' ? (
              <div className="space-y-4">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800 font-medium mb-2">⚠️ Warning</p>
                  <p className="text-sm text-yellow-700">
                    This permanently removes the notes you choose below. This action cannot be undone.
                  </p>
                </div>
                <fieldset className="space-y-1">
                  <legend className="block text-sm font-medium mb-1">Remove:</legend>
                  {([
                    ['class', "The class's notes only"],
                    ['events', `Notes on every class event (${events.filter(e => e.notes).length} with notes)`],
                    ['both', 'Both'],
                  ] as const).map(([value, label]) => (
                    <label key={value} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="radio" name="remove-what" value={value} checked={removeWhat === value} onChange={() => setRemoveWhat(value)} />
                      {label}
                    </label>
                  ))}
                </fieldset>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Type <strong>"delete"</strong> to confirm:
                  </label>
                  <input
                    type="text"
                    className="input-field w-full focus-ring"
                    value={confirmDelete}
                    onChange={(e) => setConfirmDelete(e.target.value)}
                    placeholder="delete"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <button
                    className="btn-secondary focus-ring"
                    onClick={onClose}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-primary bg-red-600 hover:bg-red-700 focus-ring"
                    onClick={handleRemoveAllNotes}
                    disabled={submitting || confirmDelete !== 'delete'}
                  >
                    {submitting ? 'Removing...' : 'Remove All Notes'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Apply to:</label>
                  <select
                    className="input-field w-full focus-ring"
                    value={editTarget}
                    onChange={(e) => setEditTarget(e.target.value as 'topics' | 'notes')}
                  >
                    <option value="topics">Topics</option>
                    <option value="notes">Class Notes</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Date Range:</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                      <input
                        type="date"
                        className="input-field w-full focus-ring"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">End Date</label>
                      <input
                        type="date"
                        className="input-field w-full focus-ring"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  {startDate && endDate && new Date(startDate) > new Date(endDate) && (
                    <p className="text-xs text-red-600 mt-1">Start date must be before end date</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Edit Mode:</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="editMode"
                        value="prefix"
                        checked={editMode === 'prefix'}
                        onChange={() => setEditMode('prefix')}
                        className="focus-ring"
                      />
                      <span className="text-sm">Add at the beginning (prefix)</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="editMode"
                        value="suffix"
                        checked={editMode === 'suffix'}
                        onChange={() => setEditMode('suffix')}
                        className="focus-ring"
                      />
                      <span className="text-sm">Add at the end (suffix)</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Text to add:</label>
                  <input
                    type="text"
                    className="input-field w-full focus-ring"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    placeholder={editMode === 'prefix' ? 'e.g., "Confirmation Class - "' : 'e.g., " - Make-up Class"'}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {editMode === 'prefix' 
                      ? 'This text will be added at the beginning of each matching event\'s ' + (editTarget === 'topics' ? 'topic' : 'notes')
                      : 'This text will be added at the end of each matching event\'s ' + (editTarget === 'topics' ? 'topic' : 'notes')
                    }
                  </p>
                </div>

                {startDate && endDate && events.filter(ev => {
                  if (!ev.event_date) return false
                  const evDate = new Date(ev.event_date)
                  return evDate >= new Date(startDate) && evDate <= new Date(endDate)
                }).length > 0 && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      This will affect{' '}
                      <strong>
                        {events.filter(ev => {
                          if (!ev.event_date) return false
                          const evDate = new Date(ev.event_date)
                          return evDate >= new Date(startDate) && evDate <= new Date(endDate)
                        }).length}
                      </strong>{' '}
                      event{events.filter(ev => {
                        if (!ev.event_date) return false
                        const evDate = new Date(ev.event_date)
                        return evDate >= new Date(startDate) && evDate <= new Date(endDate)
                      }).length !== 1 ? 's' : ''} in the selected date range.
                    </p>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-4">
                  <button
                    className="btn-secondary focus-ring"
                    onClick={onClose}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-primary focus-ring"
                    onClick={handleBulkEdit}
                    disabled={submitting || !startDate || !endDate || !editText.trim() || (!!startDate && !!endDate && new Date(startDate) > new Date(endDate))}
                  >
                    {submitting ? 'Updating...' : 'Apply Bulk Edit'}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

